/**
 * Data Validation Utilities for CenterId Tenant Isolation
 *
 * These utilities ensure that API responses only contain data
 * from the user's assigned center, providing a frontend safeguard
 * against tenant isolation failures.
 */
import { logger } from './logger';

/**
 * Validates that all items in data belong to the specified centerId
 *
 * @param {Array|Object} data - API response data (array or single object)
 * @param {string} centerId - The user's center ID to validate against
 * @returns {boolean} - True if all data belongs to centerId or has no centerId field
 */
export const validateCenterData = (data, centerId) => {
  // Handle null or undefined data
  if (!data) {
    return true;
  }

  // If data is an array, validate all items
  if (Array.isArray(data)) {
    return data.every(item => {
      // If item has centerId field, it must match user's centerId
      if (item && item.centerId) {
        return item.centerId === centerId;
      }
      // If item doesn't have centerId field, it's valid (global data)
      return true;
    });
  }

  // Single object validation
  if (typeof data === 'object' && data !== null) {
    // If object has centerId field, it must match user's centerId
    if (data.centerId) {
      return data.centerId === centerId;
    }
    // If no centerId field, it's valid (global data)
    return true;
  }

  // Primitive values are always valid
  return true;
};

/**
 * Filters array or object to only include items matching the centerId
 *
 * This function is used as a safeguard when validation fails.
 * It removes any items that don't belong to the user's center.
 *
 * @param {Array|Object} data - API response data
 * @param {string} centerId - The user's center ID to filter by
 * @returns {Array|Object} - Filtered data (same structure as input)
 */
export const filterByCenterId = (data, centerId) => {
  // Handle null or undefined data
  if (!data) {
    return data;
  }

  // If data is an array, filter items
  if (Array.isArray(data)) {
    return data.filter(item => {
      // Keep items without centerId (global data)
      if (!item || !item.centerId) {
        return true;
      }
      // Keep items matching user's centerId
      return item.centerId === centerId;
    });
  }

  // If single object, return as-is if it matches or has no centerId
  if (typeof data === 'object' && data !== null) {
    if (!data.centerId || data.centerId === centerId) {
      return data;
    }
    // Return empty object if centerId doesn't match
    return {};
  }

  // Return primitive values as-is
  return data;
};

/**
 * Logs validation information for debugging
 *
 * @param {string} operation - Description of the operation
 * @param {string} centerId - User's center ID
 * @param {boolean} isValid - Whether validation passed
 * @param {number} itemCount - Number of items (if applicable)
 */
export const logValidation = (operation, centerId, isValid, itemCount = null) => {
  const status = isValid ? '✓' : '✗';
  const countStr = itemCount !== null ? ` (${itemCount} items)` : '';
  logger.api(
    `${operation} - Center: ${centerId} - Validation: ${status}${countStr}`
  );
};